/**
 * Every string the Access & Permissions tab shows (PD-4070), mirroring
 * CreateUserWizard/copy.js so the wording can be reviewed without reading the
 * components — and so the self-modification sentence has one definition. It is
 * used twice: as the tooltip on the disabled role controls, and as the message
 * for the 422 if a request gets past them.
 */

import EMPTY_VALUE from '../../../shared/emptyValue';

export const ACCOUNTS_SECTION = {
  title: 'Accounts',
  subtitle: 'Manage the accounts associated with this user.',
  add: 'Add To Account',
  remove: 'Remove Access',
  restore: 'Restore Access',
  // One line rather than the design's three text nodes: they read as a single
  // sentence and splitting them buys nothing but three more elements.
  //
  // Both columns are nullable on portal.dealers, and a template literal
  // stringifies null to "null" where the JSX interpolation this replaced
  // rendered nothing. Fall back to the placeholder the users list already
  // shows for an absent value.
  meta: (decisivId, dealerType) =>
    `Account ID: ${decisivId || EMPTY_VALUE} · Account Type: ${dealerType ||
      EMPTY_VALUE}`,
};

export const PERMISSIONS_SECTION = {
  title: 'Permissions & Discoverability',
  subtitle:
    "Manage permissions and discoverability associated with this user's accounts.",
  applyToAll: 'Apply to All Accounts',
  customPerAccount: 'Custom per Account',
  save: 'Save Changes',
  saving: 'Saving...',
  cancel: 'Cancel',
};

// The single row "Apply to All Accounts" collapses to. Team and account both
// read as one thing, so the table needs no branch for the mode.
export const ALL_TEAMS_ROW = 'All Teams';
export const ALL_ACCOUNTS_ROW = 'All Accounts';

export const BANDS = {
  permissions: 'Permissions',
  discoverability: 'Discoverability',
};

// Keyed by the server's stable option id, falling back to the option's own
// label. That is how "Management Role" becomes the design's "Asset Mgmt." column
// without changing what the wizard shows, and how a role added server-side gets
// a sensible heading before anyone touches this file.
export const COLUMN_TITLES = {
  account: 'Account',
  'fleet-administrator': 'Fleet Admin',
  'report-user': 'Report',
  'management-role': 'Asset Mgmt.',
  tier: 'Mgmt. Role',
  visibleAsExternalRecipient: 'External',
  visibleAsInternalRecipient: 'Internal',
};

// Positive, because the columns are: a ticked box means the user IS shown.
// The legacy form and the Create User wizard ask the opposite question ("Hide
// from…"), so these say plainly which way round this table reads.
export const COLUMN_INFO = {
  visibleAsExternalRecipient:
    'When checked, this user appears to external users (provider contacts) in the recipients lists for the Notes sections of a case.',
  visibleAsInternalRecipient:
    'When checked, this user appears to internal users and contacts (at my location) in the recipients lists for the Notes sections of a case.',
};

export const LOCKS = {
  // The exact sentence the ticket specifies, and the same one the 422 maps to.
  selfModify: 'You cannot modify your own roles.',
  // Names the way out rather than only refusing: removing the last role is the
  // account-removal flow (PD-4069).
  lastRole:
    'This is the last permission on this account. Use REMOVE ACCESS to remove the user from this account.',
  // Word for word the server's decisiv.management.users.last_account, which is
  // what the 422 carries if a request gets past the disabled button. The rule is
  // enforced twice (RemoveUserAccountAccess#last_active_account? and
  // useAccountAccess's canActOn), so it must not be worded twice.
  lastAccount:
    'This user must have access to at least one account. Add them to another account before removing this one.',
};

/**
 * The subject a "not available" tooltip speaks about, keyed by the server's
 * option id. Reporting is subscribed per location, so its column reads about the
 * service rather than about the role name (PD-4723) — "Reporting is not
 * available" is what an admin looking at the location's Services tab would
 * recognise, where "Report User" names a role they cannot find there.
 *
 * Anything absent falls back to the column heading, so a service-gated role
 * added server-side gets a sensible sentence before anyone touches this file.
 */
const LOCK_SUBJECTS = {
  'report-user': 'Reporting',
};

/**
 * Why a role column is greyed out on a row: the account cannot grant it, because
 * it does not subscribe to the service the role hangs off.
 *
 * The shared "Apply to All Accounts" row grants to every account at once, so it
 * speaks in the plural and names the way out — the per-account rows can still
 * offer the column to the accounts that do have it.
 *
 * @param {string} optionId the server's stable option id, e.g. 'report-user'.
 * @param {string} title the column heading, used when the id has no subject.
 * @param {boolean} shared whether this is the Apply-to-All row.
 */
export const notAvailableLock = (optionId, title, { shared = false } = {}) => {
  const subject = LOCK_SUBJECTS[optionId] || title;

  return shared
    ? `${subject} is not available for every one of these accounts. Use Custom per Account.`
    : `${subject} is not available for this account.`;
};

export const NOTICES = {
  // Switching to Apply to All when the accounts disagree cannot preserve all of
  // them, so the row opens blank and says so — which also fails validation and
  // keeps SAVE CHANGES disabled until the admin chooses.
  overwrite:
    'These accounts currently have different permissions. Choosing permissions here will apply them to every account.',
  permissionsRequired:
    'Each account needs at least one permission. Accounts with none are highlighted below.',
};

export const MESSAGES = {
  saved: 'Access and permissions updated.',
  failed: 'Access and permissions not updated.',
  generic: 'This account could not be updated.',
};

export const ADD_TO_ACCOUNT = {
  title: 'Add User to Account',
  subtitle: 'Add a user and grant them access to an account.',
  team: 'Team',
  teamPlaceholder: 'All Teams',
  teamHelp: 'Choose one team from the list.',
  accountLabel: 'Account',
  accountHelp:
    'Choose one or multiple accounts from the list, or type to search by name.',
  accountSearchPlaceholder: 'Search accounts by name',
  accountToggle: 'Show account options',
  accountLoadingMore: 'Loading more accounts...',
  accountEmpty: 'No accounts available.',
  accountPaged: (loaded, total) => `Showing ${loaded} of ${total} accounts`,
  submit: 'Add User',
  submitting: 'Adding...',
  cancel: 'Cancel',
  failedTitle: 'Not added.',
};
